import { loadGoogleMapsApi } from '@deliveroo/remote-script-loader';

const TILE_SIZE = { height: 256, width: 256 }; // google World tile size, as of v3.22
const ZOOM_MAX = 21; // max google maps zoom level, as of v3.22
const BUFFER = 50; // edge buffer for fitting markers within viewport bounds

export default class GoogleMap {
  /**
   * Loads the Google Maps API with an optional googleApiKey as well as create a promise so that
   * when it loads, we have a callback to initialise and create a map.
   *
   * @param {string} googleApiKey - Optional googleApiKey to provide when loading Google Maps API
   */
  load(googleApiKey) {
    this.loaded = true;

    return loadGoogleMapsApi({ googleApiKey });
  }

  /**
   * Creates a Google Map map with any options from the Google Map API
   *
   * @param {DOM element} mapEl - The DOM element to replace with the map
   * @param {Object} options - Optional options to pass to the google.maps.Map class when creating a
   *   map. Valid options:
   *   https://developers.google.com/maps/documentation/javascript/reference/3/#MapOptions
   */
  createMap(mapEl, options = {}) {
    if (!this.map && !!mapEl) {
      this.map = new window.google.maps.Map(mapEl, options);
    }
  }

  /**
   * Add markers to the map
   *
   * @param {Marker[]} markerLocations - Array of Markers to place the markers and details how they
   *   should be displayed
   *
   * @typedef {Object} Marker
   * @property {Object} Marker.position - The position to place the marker
   * @property {number} Marker.position.lat - The latitudinal position
   * @property {number} Marker.position.lng - The longitudinal position
   * @property {Object} Marker.icon - The marker icon to display
   * @property {string} Marker.icon.url - The url of the image for the marker
   * @property {Size} Marker.icon.size - The size of the icon. This will crop the image if it's
   *   bigger than the size
   * @property {Point} Marker.icon.origin - The position of the image which affects where it will
   *   scale from
   * @property {Point} Marker.icon.anchor - The position of the icon relative to the
   *   Marker.position
   * @property {Size} Marker.icon.scaledSize - The size to scale the icon to
   */
  addMarkers(markerLocations) {
    if (this.map && !this.markerLocations) {
      this.markerLocations = markerLocations.map((location) => {
        const {
          id,
          position,
          icon: { url, size, origin, anchor, scaledSize },
        } = location;

        const icon = {
          url,
          size: size ? new window.google.maps.Size(...size) : null,
          origin: origin ? new window.google.maps.Point(...origin) : null,
          anchor: anchor ? new window.google.maps.Point(...anchor) : null,
          scaledSize: scaledSize ? new window.google.maps.Size(...scaledSize) : null,
        };

        const marker = new window.google.maps.Marker({
          map: this.map,
          position,
          icon,
        });

        // Added so that we can update the exact marker later
        marker.id = id;

        return marker;
      });
    }
  }

  /**
   * Move the markers that were previously already created. This won't affect how the marker looks
   *
   * @param {Location[]} newMarkerLocations - Array of locations to place the markers
   *
   * @typedef {Object} Location
   * @property {Object} Location.position - The position to place the marker
   * @property {number} Location.position.lat - The latitudinal position
   * @property {number} Location.position.lng - The longitudinal position
   */
  updateMarkers(newMarkerLocations) {
    this.markerLocations.forEach((location) => {
      const newMarkerLocation = newMarkerLocations.find(
        (newMarker) => location.id === newMarker.id,
      );

      if (!newMarkerLocation) return;

      const currentLocation = {
        lat: parseFloat(location.position.lat().toFixed(7)),
        lng: parseFloat(location.position.lng().toFixed(7)),
      };

      const newLocation = {
        lat: parseFloat(newMarkerLocation.position.lat.toFixed(7)),
        lng: parseFloat(newMarkerLocation.position.lng.toFixed(7)),
      };

      if (!(currentLocation.lat === newLocation.lat && currentLocation.lng === newLocation.lng)) {
        location.setPosition(newLocation);
      }
    });
  }

  /**
   * Returns the map's DOM element
   */
  getMapEl() {
    return this.map.getDiv();
  }

  /**
   * Zooms in and pans to the markers such that they are all visible. If a safeZone is provided, it
   * will make sure the markers are within the safeZone. Providing an offset means that the map can
   * offset this safeZone in the instance that the safeZone isn't at the center of the map
   *
   * @param {Object} Options - Options that detail how to zoom and pan to the markers
   * @param {Object} Options.safeZone - Area to make sure all the markers are within, in the
   *   instance you don't want the pins to fill the whole map are
   * @param {number} Options.safeZone.width - The width in pixels
   * @param {number} Options.safeZone.height - The height in pixels
   * @param {Object} Options.offset - An offset to reposition the map or to reposition the safe zone
   * @param {number} Options.offset.x - Distance in pixels to offset from the left
   * @param {number} Options.offset.y - Distance in pixels to offset from the top
   */
  zoomToMarkers({ safeZone, offset } = {}) {
    const { map, markerLocations } = this;

    if (map && markerLocations) {
      const bounds = this.getMarkerBounds(markerLocations);
      const boundsCenterAsLatlng = bounds.getCenter();
      const mapElement = this.getMapEl();
      const mapWidth = safeZone ? safeZone.width : mapElement.clientWidth;
      const mapHeight = safeZone ? safeZone.height : mapElement.clientHeight;
      const dimensions = this.getMapDimensions(mapWidth, mapHeight);

      const zoomLevel = this.getBoundsZoomLevel(bounds, dimensions);
      if (zoomLevel) {
        map.setZoom(zoomLevel);
      }

      if (offset) {
        setTimeout(() => {
          const offsetCenterAsLatlng = this.getOffsetCenter(map, boundsCenterAsLatlng, offset);
          map.panTo(offsetCenterAsLatlng);
        }, 200);
      }
    }
  }

  /**
   * Removes a bit from the given map (or safe zone) dimensions which acts as a buffer for so that
   * the markers aren't added right at the edge. This is especially important given the size of the
   * icons for the markers
   *
   * @param {number} width - The width of the map (or safe zone)
   * @param {number} height - The width of the map (or safe zone)
   *
   * @return {Dimensions} - The new dimension with some buffer taken out
   *
   * @typedef {Object} Dimensions
   * @property {number} Dimensions.width - The width of the dimensions
   * @property {number} Dimensions.height - The height of the dimensions
   */
  getMapDimensions(width = 0, height = 0) {
    const mapBuffer = BUFFER * 2;
    const newWidth = width > mapBuffer ? width - mapBuffer : 0;
    const newHeight = height > mapBuffer ? height - mapBuffer : 0;

    return {
      width: newWidth,
      height: newHeight,
    };
  }

  /**
   * Given an array of locations, it will return a Google Maps LatLngBounds which illustrates the
   * outer most points of the markers (shown by a North East point and a South East point)
   *
   * @param {Array} locations - An array of locations. The location should be a Google Maps Marker
   *   which has methods to get the position in latitude and longitude
   *
   * @return {LatLngBounds} - A Google Maps LatLngBounds to denote the outer most points of the
   *   markers
   */
  getMarkerBounds(locations) {
    let bounds;

    if (locations && locations.length >= 1) {
      // Reduces all the locations so that we have the outer most points to form a bound
      const { northEastLat, northEastLng, southWestLat, southWestLng } = [...locations]
        .splice(1)
        .reduce(
          (acc, location) => ({
            northEastLat: Math.max(location.position.lat(), acc.northEastLat),
            northEastLng: Math.min(location.position.lng(), acc.northEastLng),
            southWestLat: Math.min(location.position.lat(), acc.southWestLat),
            southWestLng: Math.max(location.position.lng(), acc.southWestLng),
          }),
          {
            northEastLat: locations[0].position.lat(),
            northEastLng: locations[0].position.lng(),
            southWestLat: locations[0].position.lat(),
            southWestLng: locations[0].position.lng(),
          },
        );

      // Create a new Google Maps LatLngBounds from these outer most points
      const northEast = new window.google.maps.LatLng(northEastLat, northEastLng);
      const southWest = new window.google.maps.LatLng(southWestLat, southWestLng);
      bounds = new window.google.maps.LatLngBounds();
      bounds.extend(northEast);
      bounds.extend(southWest);
    }

    return bounds;
  }

  /**
   * Translates the latitude into a point coordinate
   * The mapping between latitude, longitude and pixels is defined by the web mercator projection.
   * The latitude especially is deformed the closer the point is to the poles and so Google Maps has
   * created a formula to approximise the latitude (the web mercator projection).
   * https://developers.google.com/maps/documentation/javascript/examples/map-coordinates
   *
   * @param {number} lat - the latitude to translate the latitude into a point accounting for the
   *   web mercator projection
   *
   * @return {number} - Y axis point coordinate in pixels that is the translated form of the
   *   latitude
   */
  getMercatorProjectedLatAsPointCoord(lat) {
    // Convert latitude (degrees) into radians as Math.sin expects and returns angles in radians
    const latAsRadians = lat * (Math.PI / 180);
    let siny = Math.sin(latAsRadians);

    // Truncating to 0.9999 effectively limits latitude to 89.189. This is about a third of a tile
    // past the edge of the world tile.
    siny = Math.min(Math.max(siny, -0.9999), 0.9999);

    // The point coordinate is returned as I believe the transformation step of multiplying and
    // dividing by 180 is already handled in Google's formula to convert the radian to degree to
    // point coordinate
    // The 180 is because Google Map latitude is measured from -90 to 90

    // I also believe Google Map uses this inverse hyperbolic Gudermannian function to simplify
    // complex numbers when calculating the Mercator Projection
    // Math.log((1 + siny) / (1 - siny)) / 2

    return Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI);
  }

  /**
   * Returns the zoom that fits for a particular direction.
   *
   * @param {number} mapDimensionInPx - the map's dimension in pixels
   * @param {number} tileDimensionInPx - the map tile's dimension in pixels
   * @param {number} latLngAsPointCoord - the lat/lng difference between the markers in point
   *   coordinate pixels
   *
   * @return {number} - The zoom level
   */
  getLatLngZoom(mapDimensionInPx, tileDimensionInPx, latLngAsPointCoord) {
    // This is how many tiles fit the map
    const numberOfTiles = mapDimensionInPx / tileDimensionInPx;
    // This is how of the above tiles fit the distance given
    const numberOfTilesPerDistance = numberOfTiles / latLngAsPointCoord;

    // I believe that since each Google Maps zoom level the scale doubles, this calculates the
    // closest power of 2, on a logarithmic scale, for the given distance
    return Math.floor(Math.log(numberOfTilesPerDistance) / Math.LN2);
  }

  /**
   * Returns a zoom level where all the markers, given by the bounds, fit inside the dimensions
   * given
   *
   * @param {LatLngBounds} bounds - A Google Maps LatLngBounds to denote the outer most points of
   *   the markers
   * @param {Object} dimensions - The dimensions of the map (or safe zone) with some buffer taken
   *   out
   * @param {number} dimensions.width - The width of the dimensions
   * @param {number} dimensions.height - The height of the dimensions
   *
   * @return {number} - The zoom level
   */
  getBoundsZoomLevel(bounds, dimensions) {
    let zoomLevel;

    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();

    // This will always result in a positive latDiffAsPointCoord
    const latDiffAsPointCoord =
      this.getMercatorProjectedLatAsPointCoord(northEast.lat()) -
      this.getMercatorProjectedLatAsPointCoord(southWest.lat());

    let lngDiff = northEast.lng() - southWest.lng();
    // Longitude wraps around and the angle needs to be positive
    lngDiff = lngDiff < 0 ? lngDiff + 360 : lngDiff;
    // The 360 is because Google Map longitude is measured from -180 to 180
    const lngDiffAsPointCoord = lngDiff / 360;

    if (!!latDiffAsPointCoord || !!lngDiffAsPointCoord) {
      // Get the zoom that's appropriate for both the latitude and longitude to compare
      const latZoomLevel = this.getLatLngZoom(
        dimensions.height,
        TILE_SIZE.height,
        latDiffAsPointCoord,
      );
      const lngZoomLevel = this.getLatLngZoom(
        dimensions.width,
        TILE_SIZE.width,
        lngDiffAsPointCoord,
      );

      // Filter out NaN zoom levels as that will break Math.min
      const acceptableZoomLevels = [latZoomLevel, lngZoomLevel].filter((zoom) => !!zoom);

      // Return the lowest zoom level which is the one that is zoomed out the most. This is to
      // ensure we see all the markers (lowest = world level, highest = streets/buildings level)
      zoomLevel = Math.min(...acceptableZoomLevels, ZOOM_MAX);
    }

    return zoomLevel;
  }

  /**
   * Calculates the offset for the map based on the map's original center and returns a new center
   * for the map to pan to. It uses the map's zoom and projection which is used to convert distance
   * between latitude & longitude and a Point which then allows us to convert a point to pixels.
   * This allows us to use pixels to offset the map which is translated into latitude and longitude.
   *
   * @param {Map} map - A Google Maps Map
   * @param {LatLng} boundsCenterAsLatlng - A Google Maps LatLng for the original center of all the
   *   markers
   * @param {Object} offsetAsPixels - The pixel values to offset the map from the center
   * @param {number} offsetAsPixels.x - How much in pixels to offset the map from the left
   * @param {number} offsetAsPixels.y - How much in pixels to offset the map from the top
   *
   * @return {LatLng} - A Google Maps LatLng for the new center offset by the given amount
   */
  getOffsetCenter(map, boundsCenterAsLatlng, offsetAsPixels) {
    // Creates a Google Maps Projection so that we can convert between LatLng and Point based on the
    // map's current circumstance (such as it's zoom)
    const projection = map.getProjection();
    if (!projection) return boundsCenterAsLatlng;

    // For Google Maps each zoom level the scale doubles
    const scale = 2 ** map.getZoom();

    // Convert the center from a Google Maps LatLng to a Google Maps Point as this is the most
    // stable form to offset from
    const boundsCenterAsPoint = projection.fromLatLngToPoint(boundsCenterAsLatlng);

    // Convert the offset from pixels to a Google Maps Point
    const offsetAsPoint = new window.google.maps.Point(
      offsetAsPixels.x / scale,
      offsetAsPixels.y / scale,
    );

    // Offset from the center of the bounds
    const newCenterAsPoint = new window.google.maps.Point(
      boundsCenterAsPoint.x - offsetAsPoint.x,
      boundsCenterAsPoint.y - offsetAsPoint.y,
    );

    // Convert the new center to a Google Maps LatLng as that is the format Google Map uses to pan
    // or center
    const newCenterAsLatLng = projection.fromPointToLatLng(newCenterAsPoint);

    return newCenterAsLatLng;
  }
}
